import { Box, Divider, Stack } from '@mui/material';
import { useEffect, useState } from 'react';

import CopyableCode from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { SetupOwnCloudState } from './SetupOwnCloudFlow';
import { BulletItem } from './bulletItem';
import { FormTextField } from './formTextField';
import { NumericBulletSection } from './numericBulletSection';
import { OwnCloudErrorDialog } from './ownCloudErrorDialog';

export const ConfigureManagementAccess = (
  props: StepProps<SetupOwnCloudState>
) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const dal = useDAL();
  const azureEonApplicationResponse = dal.azure.getAzureEonApplication();

  useEffect(() => {
    if (azureEonApplicationResponse.body) {
      props.setWizardState({
        ...props.wizardState,
        eonBackupApplicationId: azureEonApplicationResponse.body.applicationId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [azureEonApplicationResponse.body]);

  const code = `\
module "azure_backup_app" {
  source = "https://eon.blob.core.windows.net/public/onboarding/v1.1.2/terraform/backup_app.zip"

  eon_backup_app_id            = "${props.wizardState.eonBackupApplicationId}"
  is_customer_app_multi_tenant = false
  customer_app_name            = "${props.wizardState.eonBackupApplicationName}"
} `;

  const validateInput = () => {
    // GUID regex pattern
    const guidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    const hasAllFields =
      !!props.wizardState.eonBackupApplicationName &&
      !!props.wizardState.eonBackupApplicationServicePrincipalId &&
      !!props.wizardState.customerBackupAppId &&
      !!props.wizardState.customerBackupAppServicePrincipalId;

    const hasValidGuids =
      guidRegex.test(
        props.wizardState.eonBackupApplicationServicePrincipalId || ''
      ) &&
      guidRegex.test(props.wizardState.customerBackupAppId || '') &&
      guidRegex.test(
        props.wizardState.customerBackupAppServicePrincipalId || ''
      );

    return hasAllFields && hasValidGuids;
  };

  const onNextClick = async () => {
    if (!validateInput()) {
      console.log('Invalid input - check the fields and try again');
      setError(new Error('Please fill in all fields and try again'));
      return;
    }

    try {
      setIsDeploying(true);
      await dal.onboarding.appModule.deploy({
        eonBackupApplicationName: props.wizardState.eonBackupApplicationName,
        eonBackupApplicationServicePrincipalId:
          props.wizardState.eonBackupApplicationServicePrincipalId!,
        customerBackupAppId: props.wizardState.customerBackupAppId!,
        customerBackupAppServicePrincipalId:
          props.wizardState.customerBackupAppServicePrincipalId!,
      });
    } catch (error) {
      setError(error as Error);
    } finally {
      setIsDeploying(false);
    }

    props.setNextStep(props.currentStep.next?.[0]);
  };

  return (
    <>
      <StepContainer
        stepperIndex={0}
        stepperLabels={props.stepperLabels}
        onBackClick={props.back}
        canGoNext={() =>
          !isDeploying &&
          !!props.wizardState.eonBackupApplicationName &&
          !!props.wizardState.eonBackupApplicationServicePrincipalId &&
          !!props.wizardState.customerBackupAppId &&
          !!props.wizardState.customerBackupAppServicePrincipalId
        }
        nextButtonText={'Connect & Continue'}
        sx={{ overflow: 'auto' }}
        onNextClick={onNextClick}
      >
        <Box paddingX='min(100px,6vw)' width='100%' height='100%'>
          <Stack
            gap='40px'
            divider={<Divider sx={{ borderStyle: 'dashed' }} />}
          >
            <NumericBulletSection
              bulletNumber={1}
              title='Deploy Eon’s management access'
            >
              <BulletItem
                title='Fill in the details to generate your Terraform module, then review and deploy it in your environment.'
                leftContent={
                  <FormTextField
                    title='Eon backup application name'
                    placeholder='Enter a name for your Eon backup application'
                    value={props.wizardState.eonBackupApplicationName || ''}
                    onChange={(event) =>
                      props.setWizardState({
                        ...props.wizardState,
                        eonBackupApplicationName: event.target.value,
                      })
                    }
                  />
                }
                rightContent={<CopyableCode title='main.tf' code={code} />}
              />
            </NumericBulletSection>
            <NumericBulletSection
              bulletNumber={2}
              title='Complete configuration'
            >
              <BulletItem
                title='Bring back the Terraform outputs to complete this step.'
                leftContent={
                  <Stack gap='24px'>
                    <FormTextField
                      title='eon_backup_app_service_principal_id'
                      placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                      value={
                        props.wizardState
                          .eonBackupApplicationServicePrincipalId || ''
                      }
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          eonBackupApplicationServicePrincipalId:
                            event.target.value,
                        })
                      }
                    />
                    <FormTextField
                      title='customer_backup_app_id'
                      placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                      value={props.wizardState.customerBackupAppId || ''}
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          customerBackupAppId: event.target.value,
                        })
                      }
                    />
                    <FormTextField
                      title='customer_backup_app_service_principal_id'
                      placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                      value={
                        props.wizardState.customerBackupAppServicePrincipalId ||
                        ''
                      }
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          customerBackupAppServicePrincipalId:
                            event.target.value,
                        })
                      }
                    />
                  </Stack>
                }
              />
            </NumericBulletSection>
          </Stack>
        </Box>
      </StepContainer>
      <OwnCloudErrorDialog
        isOpen={!!error}
        onClose={() => setError(null)}
        error={error}
      />
    </>
  );
};
